import { Button } from '@ai-tutor/ui';
import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { notify } from '@/lib/notify';

/**
 * Renders Stripe's PaymentElement inside an Elements provider that's
 * been initialized with a SetupIntent client_secret. On submit, calls
 * ``stripe.confirmSetup`` which (a) collects the card / wallet, (b)
 * walks the user through any 3DS challenge in-place, and (c) attaches
 * the PaymentMethod to the customer.
 *
 * The parent component owns the SetupIntent lifecycle - it fetches the
 * client_secret, renders ``<StripeProvider clientSecret=...>``, and
 * mounts this form inside it.
 */
export interface AddPaymentMethodFormProps {
  /** Return URL used by Stripe when a redirect-based payment method
   *  (BACS Direct Debit, etc.) needs to bounce the user out. For
   *  card-only flows this is rarely hit, but Stripe requires the field. */
  returnUrl: string;
  /** Fired after a successful confirm. The parent should refresh its
   *  ``listPaymentMethods`` query and unmount this form. */
  onSuccess: () => void;
  /** Optional secondary action label (e.g. "Cancel"). When omitted, the
   *  form shows only the primary submit button. */
  onCancel?: () => void;
  submitLabel?: string;
}

export function AddPaymentMethodForm({
  returnUrl,
  onSuccess,
  onCancel,
  submitLabel = 'Save card',
}: AddPaymentMethodFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setErrorMsg(null);

    // ``redirect: 'if_required'`` tells Stripe to handle 3DS challenges
    // inline (popup / iframe) when possible. Only fall back to a full
    // redirect for payment methods that genuinely require it.
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    });

    if (error) {
      // ``error.message`` is already user-friendly per Stripe's UX rules.
      setErrorMsg(error.message || 'Could not save card.');
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      notify.success('Card saved');
      onSuccess();
    } else {
      // Pending status - e.g. processing or requires_action that wasn't
      // handled by the inline 3DS. Surface a soft message; the webhook
      // will eventually finalize.
      notify.info(
        'Your card is processing. It will appear in your payment methods shortly.'
      );
      onSuccess();
    }
    setSubmitting(false);
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid='add-payment-method-form'
      className='space-y-4'
    >
      <PaymentElement options={{ layout: 'tabs' }} />

      {errorMsg && (
        <div
          data-testid='add-payment-method-error'
          className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-950/40 dark:text-red-300'
        >
          {errorMsg}
        </div>
      )}

      <div className='flex justify-end gap-2'>
        {onCancel && (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='max-sm:h-11'
            onClick={onCancel}
            disabled={submitting}
          >
            Cancel
          </Button>
        )}
        <Button
          data-testid='save-card-button'
          type='submit'
          size='sm'
          className='max-sm:h-11'
          disabled={!stripe || !elements || submitting}
        >
          {submitting ? (
            <>
              <Loader2 className='mr-1 h-4 w-4 animate-spin' />
              Saving…
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </form>
  );
}
